import { useContext, useEffect, useState, JSX } from "react";
import { capitalize } from "lodash-es";
import { useRouter } from "next/router";
import classNames from "classnames";
import { Heading } from "@otrium/atoms";
import { Trans, useLingui } from "@lingui/react";
import { msg } from "@lingui/macro";
import layout from "src/styles/layout.module.scss";
import styles from "./CategoryView.module.scss";
import { PopularCategories } from "src/organisms/PopularCategories";
import { locale } from "src/config/app-config";
import { Gender } from "src/modules/page";
import { ShopTypeDispatch } from "src/contexts/shop-type.context";
import { localeFix } from "src/utils/genderLocale/genderLocale";
import { getI18nRoute } from "src/utils/i18nRoutes";
import { PageHead } from "src/molecules/PageHead";
import { PopularBrands } from "src/organisms/PopularBrands";
import { Breadcrumbs, BreadcrumbsItem } from "src/molecules/Breadcrumbs";

const CategoryViewBreadcrumbs = ({ genderId }: { genderId: string }) => {
  const items: BreadcrumbsItem[] = [
    {
      name: <Trans id="Home" />,
      href: getI18nRoute({ route: `/${genderId}`, locale }),
    },
    {
      name: <Trans id={capitalize(genderId)} />,
      href: getI18nRoute({ route: `/${genderId}`, locale }),
    },
    { name: <Trans id="All categories" /> },
  ];

  return <Breadcrumbs items={items} />;
};

export default function CategoryView({
  isKidsPage = false,
}: {
  isKidsPage?: boolean;
}): JSX.Element {
  const { i18n } = useLingui();
  const [isMounted, setIsMounted] = useState(false);
  const router = useRouter();
  const { slug } = router.query;
  const localeGenderId = localeFix({
    gender: isKidsPage ? "kids" : (slug as string),
  });

  const shopTypeDispatch = useContext(ShopTypeDispatch);

  // NOTE: Source: https://github.com/vercel/next.js/issues/2473#issuecomment-587551234
  useEffect(() => setIsMounted(true), []);

  useEffect(() => {
    shopTypeDispatch?.({ type: "SET_SHOP_TYPE", payload: localeGenderId });
  }, [localeGenderId, shopTypeDispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const genderToUpper = (str: string | string[]): string =>
    Array.isArray(str)
      ? str[0].charAt(0).toUpperCase()
      : str?.charAt(0).toUpperCase();

  return (
    <>
      <div className={layout.content}>
        <PageHead
          title={i18n._(
            msg`${
              (slug && genderToUpper(slug)) as string
            } | Product categories | Otrium`
          )}
          deeplink={`otrium://product-category/${localeGenderId}`}
        />
        <CategoryViewBreadcrumbs genderId={slug as string} />
        <header>
          <Heading
            data-testid="page-title"
            as="h1"
            variant="h2"
            fontSize={["20px", "20px", "20px", "28px", "28px", "28px"]}
            mb={3}
            sx={{
              textTransform: "uppercase",
              fontWeight: "700",
              color: "black",
            }}
          >
            <Trans id="All categories" />
          </Heading>
        </header>
        <div className={styles.categories}>
          <PopularCategories />
        </div>
      </div>
      {isMounted && slug && (
        <section className={styles.popularBrands}>
          <div className={classNames(layout.bleedMobile, layout.mobileGap)}>
            <PopularBrands
              localeGenderId={localeGenderId}
              genderId={(slug as Gender) || "women"}
              pageName="category_product_listing_page"
            />
          </div>
        </section>
      )}
    </>
  );
}
